// ─── Razorpay checkout, loaded on demand ──────────────────────────────────────
// The script is ~90KB and only matters on the last step of the form, so it is
// fetched when the student presses Pay rather than on every page view.

const SRC = "https://checkout.razorpay.com/v1/checkout.js";

let pending = null;

/** Resolves once window.Razorpay exists. Concurrent calls share one fetch. */
export function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve(window.Razorpay);
  if (pending) return pending;

  pending = new Promise((resolve, reject) => {
    const done = () =>
      window.Razorpay
        ? resolve(window.Razorpay)
        : reject(new Error("Razorpay loaded but did not register"));

    // A script tag may already be in flight from an earlier mount.
    const existing = document.querySelector(`script[src="${SRC}"]`);
    if (existing) {
      existing.addEventListener("load", done, { once: true });
      existing.addEventListener("error", () => reject(new Error("Could not load Razorpay")), { once: true });
      return;
    }

    const s = document.createElement("script");
    s.src = SRC;
    s.async = true;
    s.onload = done;
    s.onerror = () => {
      pending = null;          // let a later attempt retry after a dropped connection
      s.remove();
      reject(new Error("Could not load Razorpay"));
    };
    document.body.appendChild(s);
  });

  return pending;
}

export const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

/** POST JSON and surface the server's own message on a 4xx/5xx. */
export async function postJson(path, body) {
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error("We couldn't reach the server. Check your connection and try again.");
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || "Something went wrong. Please try again.");
    // Which half of the flow gave up, and the driver's classification of why.
    err.stage  = data.stage;
    err.reason = data.reason;
    throw err;
  }
  return data;
}
